<script setup>
// Multi-select with server-side search. The host's TagMultiSelectComboBox only filters
// the options it already has and never emits `search`, so contact/conversation search in
// the card modal never reached the API. Options here are whatever the parent fetched:
// they are shown as-is (the server already matched name, phone, e-mail or number).
import { ref, computed, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';

const props = defineProps({
  options: { type: Array, required: true },
  modelValue: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  searchPlaceholder: { type: String, default: '' },
  emptyState: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'search']);

const { t } = useI18n();

const selectedValues = ref([...props.modelValue]);
const open = ref(false);
const search = ref('');
const dropdownRef = ref(null);

const selectedTags = computed(() =>
  selectedValues.value.map(
    value =>
      props.options.find(opt => opt.value === value) || { value, label: value }
  )
);

const emitSelection = () =>
  emit('update:modelValue', [...selectedValues.value]);

const toggleOption = option => {
  const index = selectedValues.value.indexOf(option.value);
  if (index === -1) selectedValues.value.push(option.value);
  else selectedValues.value.splice(index, 1);
  emitSelection();
};

const removeTag = value => {
  const index = selectedValues.value.indexOf(value);
  if (index === -1) return;
  selectedValues.value.splice(index, 1);
  emitSelection();
};

const onSearch = query => {
  search.value = query;
  emit('search', query);
};

const toggleDropdown = () => {
  if (props.disabled) return;
  open.value = !open.value;
  if (!open.value) return;
  search.value = '';
  // Load a first page so the list is not empty before the agent types.
  emit('search', '');
  nextTick(() => dropdownRef.value?.focus());
};

watch(
  () => props.modelValue,
  newValue => {
    selectedValues.value = [...newValue];
  }
);
</script>

<template>
  <div
    class="relative w-full min-w-0"
    :class="{ 'cursor-not-allowed': disabled }"
    @click.prevent
  >
    <OnClickOutside @trigger="open = false">
      <div
        class="flex flex-wrap w-full gap-2 px-3 py-2.5 border rounded-lg cursor-pointer bg-n-alpha-black2 min-h-[42px] transition-all duration-500 ease-in-out"
        :class="{
          'border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6':
            !open,
          'border-n-brand': open,
          'cursor-not-allowed pointer-events-none opacity-50': disabled,
        }"
        data-test="remote-tag-select-trigger"
        @click="toggleDropdown"
      >
        <div
          v-for="tag in selectedTags"
          :key="tag.value"
          class="flex items-center justify-center max-w-full gap-1 px-2 py-0.5 rounded-lg bg-n-alpha-black1"
          @click.stop
        >
          <span class="flex-grow min-w-0 text-sm truncate text-n-slate-12">
            {{ tag.label }}
          </span>
          <span
            class="flex-shrink-0 cursor-pointer i-lucide-x size-3 text-n-slate-11"
            @click="removeTag(tag.value)"
          />
        </div>
        <span
          v-if="selectedTags.length === 0"
          class="flex items-center text-sm text-n-slate-11"
        >
          {{ placeholder || t('COMBOBOX.PLACEHOLDER') }}
        </span>
      </div>

      <ComboBoxDropdown
        ref="dropdownRef"
        :open="open"
        :options="options"
        :search-value="search"
        :search-placeholder="searchPlaceholder"
        :empty-state="emptyState"
        :loading="isLoading"
        multiple
        :selected-values="selectedValues"
        @search="onSearch"
        @select="toggleOption"
      />
    </OnClickOutside>
  </div>
</template>
